import { produce } from 'immer';
import { isEqual } from 'lodash';
import {
  GenericOverrides,
  TimeSeriesChartSpec,
  TimeSeriesChartYAxisOptions,
  TimeSeriesOverrides,
  TimeSeriesVisualOverride,
} from '@/core';
import { OptionsEditorColumn, OptionsEditorGrid } from '@/plugin-editor-components';
import { OverrideColorPicker, OverrideRightYAxis, OverridesEditor, OverridesEditorProps } from '@/plugin-system';
import { OverrideLineStyle } from '@/plugin-system/components/editing/OverridesEditor/OverridesControls/OverrideLineStyle';
import { OverrideOptions } from '@/plugin-system/components/editing/OverridesEditor/types';
import { TimeSeriesChartOptionsEditorProps } from '../time-series-chart-model';

/*
Ok this is kinda weird.

For the right y-axis, because we edit the override AND the actual settings together 
we kind of have to combine those two here and then pull them apart when we produce the new spec
*/
export type TimeSeriesVisualOverrideYAxis = Omit<TimeSeriesVisualOverride, 'y_axis'> & {
  right_y_axis?: TimeSeriesChartYAxisOptions;
};

const options: OverrideOptions<TimeSeriesVisualOverrideYAxis> = {
  color: {
    title: 'Color',
    property: 'color',
    Component: OverrideColorPicker,
  },
  line_style: {
    title: 'Line style',
    property: 'line_style',
    Component: OverrideLineStyle,
  },
  right_y_axis: {
    title: 'Right y-axis',
    property: 'right_y_axis',
    Component: OverrideRightYAxis,
    default: {
      show: true,
    },
  },
};

export const fromOverridesToYAxisOverrides = (
  overrides: TimeSeriesOverrides | undefined,
  yAxis: TimeSeriesChartYAxisOptions | undefined
): GenericOverrides<TimeSeriesVisualOverrideYAxis> | undefined => {
  if (overrides === undefined) return overrides;

  return overrides.map((override) => {
    const newProperties: TimeSeriesVisualOverrideYAxis = { ...override.properties };

    // @ts-expect-error cleaning up type mismatch
    delete newProperties.y_axis;

    // If we've got the key for use_right_y_axis, then we need to make the right_y_axis
    if (Object.hasOwn(override.properties, 'y_axis')) {
      newProperties.right_y_axis = {
        ...yAxis,
        show: override.properties.y_axis === 'right',
      };
    }

    return {
      ...override,
      properties: newProperties,
    };
  });
};

export const fromYAxisOverridesToOverrides = (
  overrides: GenericOverrides<TimeSeriesVisualOverrideYAxis> | undefined,
  yaxisMaybeUndefined: TimeSeriesChartYAxisOptions | undefined
): [TimeSeriesOverrides | undefined, TimeSeriesChartYAxisOptions | undefined] => {
  const yaxis = yaxisMaybeUndefined === undefined ? {} : yaxisMaybeUndefined;
  const newYAxisOptions: TimeSeriesChartYAxisOptions = { ...yaxis };

  // If there are no overrides, then we wipe out the yaxis
  if (!overrides) return [overrides, undefined];

  let hasYAxisOverride = false;

  const newOverrides = overrides.map((override) => {
    const { right_y_axis: _throwaway, ...rest } = { ...override.properties };
    const newProperties: TimeSeriesVisualOverride = rest;

    // If the override has right_y_axis key, that means we need to include the use_right_y_axis value
    // even if right_y_axis is undefined, since that's just the default empty state when it gets added
    if (Object.hasOwn(override.properties, 'right_y_axis')) {
      newProperties.y_axis = override.properties.right_y_axis?.show === true ? 'right' : 'left';

      // If the right Y Axis on the override is not undefined, then we need to loop over
      // every non "show" property and add that to the yaxis options we are building IF it's
      // different than what is currently on the yaxis
      if (override.properties.right_y_axis !== undefined) {
        hasYAxisOverride = true;
        const { show: _, ...properties } = override.properties.right_y_axis;

        for (const [key, prop] of Object.entries(properties)) {
          // if the prop isn't the same as the one on the current yaxis and we don't have
          // this prop on the new yaxis options then we will take this one
          if (!isEqual(prop, yaxis[key as keyof TimeSeriesChartYAxisOptions])) {
            if (newYAxisOptions !== undefined) {
              // Having to cast prop as any here
              // It seems like this should be straight forward, it's pulling a prop
              // off a YAxis and putting it onto another YAxis, but getting types for that
              // correct is difficult
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              newYAxisOptions[key as keyof TimeSeriesChartYAxisOptions] = prop as any;
            }
          }
        }
      }
    }

    return {
      ...override,
      properties: newProperties,
    };
  });

  const newYAxisHasProperties = Object.keys(newYAxisOptions).length > 0;

  if (hasYAxisOverride) {
    return [newOverrides, newYAxisHasProperties ? newYAxisOptions : undefined];
  } else {
    return [newOverrides, undefined];
  }
};

export function TimeSeriesChartOptionsEditorAdvancedSettings(props: TimeSeriesChartOptionsEditorProps) {
  const { value, onChange } = props;

  const handleOverridesChange: OverridesEditorProps<TimeSeriesVisualOverrideYAxis>['onChange'] = (
    overridesWithYAxis
  ) => {
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        const [overrides, y_axis_right] = fromYAxisOverridesToOverrides(overridesWithYAxis, draft.y_axis_right);

        draft.overrides = overrides;
        draft.y_axis_right = y_axis_right;
      })
    );
  };

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OverridesEditor
          overrideOptions={options}
          overrides={fromOverridesToYAxisOverrides(props.value.overrides ?? [], props.value.y_axis_right)}
          onChange={handleOverridesChange}
        />
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
